import type { ReportDetailDto } from '@act/shared/data-transfer-objects';
import { useCallback, useState } from 'react';
import { reportsRequests } from 'redux/requests/reports.requests';
import { makeRequest } from 'redux/utils/apiRequest';

type ToastFn = (message: string, variant: 'success' | 'error') => void;

interface UseReportWarehouseRefreshParams {
  reportId: string;
  report: ReportDetailDto | null;
  refreshReport: () => void;
  toast: ToastFn;
}

/**
 * Exposes a manual warehouse refresh handler for report builder preview.
 * @param params - Report identity, load state, reload callback, and toast.
 * @returns Whether refresh applies, in-flight state, and `handleRefreshData`.
 */
export const useReportWarehouseRefresh = ({
  reportId,
  report,
  refreshReport,
  toast,
}: UseReportWarehouseRefreshParams) => {
  const [refreshingSnapshots, setRefreshingSnapshots] = useState(false);

  const refreshWarehouseSnapshots = useCallback(
    async (showSuccessToast: boolean) => {
      if (!reportId || !report) {
        return;
      }
      setRefreshingSnapshots(true);
      try {
        const response = await makeRequest(
          reportsRequests.refreshReportSnapshots,
          {
            params: { id: reportId },
          },
        );
        refreshReport();
        if (showSuccessToast) {
          if (response.hadFailures) {
            toast(
              'Some sections could not be refreshed; showing last saved data',
              'error',
            );
          } else {
            toast('Report data refreshed', 'success');
          }
        }
      } catch {
        toast('Could not refresh report data from the warehouse', 'error');
      } finally {
        setRefreshingSnapshots(false);
      }
    },
    [report, reportId, refreshReport, toast],
  );

  const canRefreshData = Boolean(report);

  const handleRefreshData = useCallback(() => {
    void refreshWarehouseSnapshots(true);
  }, [refreshWarehouseSnapshots]);

  return {
    canRefreshData,
    refreshingSnapshots,
    handleRefreshData,
  };
};
